import React, { useState } from 'react';
import { INDIAN_STATES } from '../../types/customer.types';
import { Vendor, CreateVendorRequest } from '../../types/vendor.types';
import {
  Field,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import { cn } from '../../lib/utils';

interface VendorFormProps {
  // When set, the form edits this vendor; otherwise it creates a new one
  vendor?: Vendor | null;
  onSubmit: (data: CreateVendorRequest) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

// The same rules the server applies
const PHONE_PATTERN = /^[+]?[0-9\s\-().]{8,20}$/;
const GST_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const stateByGstCode = (code: string) => INDIAN_STATES.find((state) => state.gstCode === code);

// A numbered group of related fields
const Section: React.FC<{
  step: number;
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ step, title, description, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white">
    <div className="flex items-center gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
        {step}
      </span>
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
      </div>
    </div>
    <div className="grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2">{children}</div>
  </section>
);

const VendorForm: React.FC<VendorFormProps> = ({ vendor, onSubmit, onCancel, isSaving }) => {
  const isEdit = !!vendor;

  const [form, setForm] = useState({
    name: vendor?.name || '',
    code: vendor?.code || '',
    email: vendor?.email || '',
    phone: vendor?.phone || '',
    status: vendor && !vendor.isActive ? 'inactive' : 'active',
    address: vendor?.address || '',
    city: vendor?.city || '',
    state: vendor?.state || '',
    pincode: vendor?.pincode || '',
    contactPerson: vendor?.contactPerson || '',
    contactPhone: vendor?.contactPhone || '',
    gstNumber: vendor?.gstNumber || '',
    panNumber: vendor?.panNumber || '',
    paymentTerms: String(vendor?.paymentTerms ?? 30),
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setField = (name: keyof typeof form, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'gstNumber') {
        next.gstNumber = value.toUpperCase().replace(/\s/g, '');
        // A complete GSTIN carries the state and the PAN, so fill in what is still empty
        if (GST_PATTERN.test(next.gstNumber)) {
          if (!prev.panNumber.trim()) next.panNumber = next.gstNumber.slice(2, 12);
          if (!prev.state) next.state = stateByGstCode(next.gstNumber.slice(0, 2))?.name || '';
        }
      }
      if (name === 'panNumber') next.panNumber = value.toUpperCase().replace(/\s/g, '');
      if (name === 'pincode') next.pincode = value.replace(/\D/g, '').slice(0, 6);
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const next: Record<string, string> = {};
    const text = (key: keyof typeof form) => form[key].trim();

    if (!text('name')) next.name = 'Vendor name is required';
    else if (text('name').length < 2 || text('name').length > 100) next.name = 'Name must be 2 to 100 characters';

    if (!text('code')) next.code = 'Vendor code is required';
    else if (text('code').length < 3 || text('code').length > 20) next.code = 'Code must be 3 to 20 characters';

    if (!text('email')) next.email = 'Email is required';
    else if (!EMAIL_PATTERN.test(text('email'))) next.email = 'Enter a valid email address';

    if (!text('phone')) next.phone = 'Phone is required';
    else if (!PHONE_PATTERN.test(text('phone'))) next.phone = 'Enter 8 to 20 digits; +, spaces, hyphens and brackets are allowed';

    if (!text('address')) next.address = 'Address is required';
    else if (text('address').length < 5 || text('address').length > 200) next.address = 'Address must be 5 to 200 characters';

    if (!text('city')) next.city = 'City is required';
    else if (text('city').length < 2 || text('city').length > 50) next.city = 'City must be 2 to 50 characters';

    if (!form.state) next.state = 'State is required';

    if (!form.pincode) next.pincode = 'Pincode is required';
    else if (!/^[0-9]{6}$/.test(form.pincode)) next.pincode = 'Pincode must be 6 digits';

    // The contact person is optional, but what is entered must be usable
    if (text('contactPerson') && (text('contactPerson').length < 2 || text('contactPerson').length > 50)) {
      next.contactPerson = 'Contact person must be 2 to 50 characters';
    }
    if (text('contactPhone') && !PHONE_PATTERN.test(text('contactPhone'))) {
      next.contactPhone = 'Enter 8 to 20 digits; +, spaces, hyphens and brackets are allowed';
    }

    // Tax numbers are optional (an unregistered vendor has no GSTIN) but must be well formed
    if (form.gstNumber && !GST_PATTERN.test(form.gstNumber)) {
      next.gstNumber = 'A GST number has 15 characters, like 27ABCDE1234F1Z5';
    } else if (form.gstNumber) {
      const gstState = stateByGstCode(form.gstNumber.slice(0, 2));
      const chosenState = INDIAN_STATES.find((state) => state.name === form.state);
      if (!gstState) {
        next.gstNumber = 'The first two digits are not a valid GST state code';
      } else if (chosenState && chosenState.gstCode !== gstState.gstCode) {
        next.gstNumber = `This GST number belongs to ${gstState.name}, but the state is ${chosenState.name}`;
      }
    }

    if (form.panNumber && !PAN_PATTERN.test(form.panNumber)) {
      next.panNumber = 'A PAN has 10 characters, like ABCDE1234F';
    } else if (form.panNumber && GST_PATTERN.test(form.gstNumber) && form.gstNumber.slice(2, 12) !== form.panNumber) {
      next.panNumber = 'The PAN does not match the PAN inside the GST number';
    }

    const paymentTerms = Number(form.paymentTerms);
    if (form.paymentTerms.trim() === '' || !Number.isInteger(paymentTerms) || paymentTerms < 0 || paymentTerms > 365) {
      next.paymentTerms = 'Enter a whole number of days from 0 to 365';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: CreateVendorRequest = {
      name: form.name.trim(),
      code: form.code.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      // Null clears a tax number that was removed in the form
      gstNumber: form.gstNumber || null,
      panNumber: form.panNumber || null,
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state,
      pincode: form.pincode,
      contactPerson: form.contactPerson.trim() || null,
      contactPhone: form.contactPhone.trim() || null,
      paymentTerms: Number(form.paymentTerms),
      isActive: form.status === 'active',
    };

    try {
      await onSubmit(payload);
    } catch (error) {
      // The caller reports the failure; keep the form open so nothing is lost
    }
  };

  const fieldClass = (key: string) => cn(inputClass, errors[key] && inputErrorClass);
  const errorCount = Object.values(errors).filter(Boolean).length;
  const paymentTerms = Number(form.paymentTerms) || 0;
  const location = [form.city.trim(), form.state].filter(Boolean).join(', ');
  // A state saved before the list existed stays selectable
  const hasUnlistedState = !!form.state && !INDIAN_STATES.some((state) => state.name === form.state);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="space-y-5 xl:col-span-2">
          <Section step={1} title="Company" description="Who the vendor is and how to reach the business.">
            <Field label="Vendor Name" htmlFor="ven-name" required error={errors.name}>
              <input
                id="ven-name"
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                className={fieldClass('name')}
                placeholder="e.g. Steel Industries Ltd"
              />
            </Field>

            <Field
              label="Vendor Code"
              htmlFor="ven-code"
              required
              error={errors.code}
              hint="A short unique code used on purchase orders."
            >
              <input
                id="ven-code"
                type="text"
                value={form.code}
                onChange={(e) => setField('code', e.target.value)}
                className={cn(fieldClass('code'), 'font-mono')}
                placeholder="e.g. STEEL001"
              />
            </Field>

            <Field label="Email" htmlFor="ven-email" required error={errors.email}>
              <input
                id="ven-email"
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                className={fieldClass('email')}
                placeholder="sales@vendor.in"
              />
            </Field>

            <Field label="Phone" htmlFor="ven-phone" required error={errors.phone}>
              <input
                id="ven-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                className={fieldClass('phone')}
                placeholder="+91-9876543210"
              />
            </Field>

            <Field
              label="Status"
              htmlFor="ven-status"
              hint="An inactive vendor is kept for records but cannot be put on new purchase orders."
            >
              <select
                id="ven-status"
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
                className={inputClass}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>
          </Section>

          <Section step={2} title="Address" description="Where the vendor is based. The state decides whether its GST is CGST + SGST or IGST.">
            <Field label="Address" htmlFor="ven-address" required error={errors.address} className="sm:col-span-2">
              <textarea
                id="ven-address"
                rows={2}
                value={form.address}
                onChange={(e) => setField('address', e.target.value)}
                className={fieldClass('address')}
                placeholder="Building, street, area"
              />
            </Field>

            <Field label="City" htmlFor="ven-city" required error={errors.city}>
              <input
                id="ven-city"
                type="text"
                value={form.city}
                onChange={(e) => setField('city', e.target.value)}
                className={fieldClass('city')}
              />
            </Field>

            <Field label="State" htmlFor="ven-state" required error={errors.state}>
              <select
                id="ven-state"
                value={form.state}
                onChange={(e) => setField('state', e.target.value)}
                className={fieldClass('state')}
              >
                <option value="">Select a state</option>
                {hasUnlistedState && <option value={form.state}>{form.state}</option>}
                {INDIAN_STATES.map((state) => (
                  <option key={state.gstCode} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Pincode" htmlFor="ven-pincode" required error={errors.pincode}>
              <input
                id="ven-pincode"
                type="text"
                inputMode="numeric"
                value={form.pincode}
                onChange={(e) => setField('pincode', e.target.value)}
                className={fieldClass('pincode')}
                placeholder="6 digits"
              />
            </Field>
          </Section>

          <Section step={3} title="Contact person" description="The person your team deals with at this vendor. Optional.">
            <Field label="Contact Person" htmlFor="ven-contact" error={errors.contactPerson}>
              <input
                id="ven-contact"
                type="text"
                value={form.contactPerson}
                onChange={(e) => setField('contactPerson', e.target.value)}
                className={fieldClass('contactPerson')}
              />
            </Field>

            <Field label="Contact Phone" htmlFor="ven-contact-phone" error={errors.contactPhone}>
              <input
                id="ven-contact-phone"
                type="tel"
                value={form.contactPhone}
                onChange={(e) => setField('contactPhone', e.target.value)}
                className={fieldClass('contactPhone')}
                placeholder="+91-9876543210"
              />
            </Field>
          </Section>

          <Section step={4} title="Tax and payment" description="GST registration, and how long you have to pay.">
            <Field
              label="GST Number"
              htmlFor="ven-gst"
              error={errors.gstNumber}
              hint="Leave empty for an unregistered vendor."
            >
              <input
                id="ven-gst"
                type="text"
                maxLength={15}
                value={form.gstNumber}
                onChange={(e) => setField('gstNumber', e.target.value)}
                className={cn(fieldClass('gstNumber'), 'font-mono')}
                placeholder="27ABCDE1234F1Z5"
              />
            </Field>

            <Field
              label="PAN Number"
              htmlFor="ven-pan"
              error={errors.panNumber}
              hint="Filled in from the GST number when left empty."
            >
              <input
                id="ven-pan"
                type="text"
                maxLength={10}
                value={form.panNumber}
                onChange={(e) => setField('panNumber', e.target.value)}
                className={cn(fieldClass('panNumber'), 'font-mono')}
                placeholder="ABCDE1234F"
              />
            </Field>

            <Field
              label="Payment Terms (Days)"
              htmlFor="ven-terms"
              error={errors.paymentTerms}
              hint="Days you have to pay after the goods arrive. 0 means on delivery."
            >
              <input
                id="ven-terms"
                type="number"
                min="0"
                max="365"
                step="1"
                value={form.paymentTerms}
                onChange={(e) => setField('paymentTerms', e.target.value)}
                className={fieldClass('paymentTerms')}
              />
            </Field>
          </Section>
        </div>

        {/* Right: the vendor so far, and what the details are used for */}
        <aside className="xl:col-span-1">
          <div className="space-y-5 xl:sticky xl:top-4">
            <section className="rounded-lg border border-gray-200 bg-white">
              <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
                Summary
              </h4>
              <div className="space-y-4 p-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Vendor</p>
                  <p className="font-medium text-gray-900">{form.name.trim() || 'Not named yet'}</p>
                  {form.code.trim() && <p className="font-mono text-xs text-gray-500">{form.code.trim()}</p>}
                </div>

                <div className="rounded-md bg-indigo-50 px-3 py-3">
                  <p className="text-xs text-indigo-700">Payment terms</p>
                  <p className="text-2xl font-semibold text-indigo-900 tabular-nums">
                    {paymentTerms > 0 ? `${paymentTerms} days` : 'On delivery'}
                  </p>
                  <p className="mt-0.5 text-xs text-indigo-700">
                    {paymentTerms > 0 ? 'to pay after the goods arrive' : 'payment is due when the goods arrive'}
                  </p>
                </div>

                <dl className="space-y-2">
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Location</dt>
                    <dd className="text-right font-medium text-gray-900">{location || '—'}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">GST</dt>
                    <dd className="text-right font-medium text-gray-900">
                      {form.gstNumber ? 'Registered' : 'Unregistered'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Contact</dt>
                    <dd className="text-right font-medium text-gray-900">{form.contactPerson.trim() || '—'}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Status</dt>
                    <dd className="font-medium text-gray-900">{form.status === 'active' ? 'Active' : 'Inactive'}</dd>
                  </div>
                </dl>
              </div>
            </section>

            <section className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
              <h4 className="text-sm font-semibold text-gray-900">Where this is used</h4>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>Active vendors can be chosen on a purchase order.</li>
                <li>Only tax paid to a vendor with a GST number counts towards input tax.</li>
                <li>A vendor in another state charges IGST instead of CGST and SGST.</li>
              </ul>
            </section>
          </div>
        </aside>
      </div>

      {/* Footer keeps the problems count in view next to the buttons */}
      <div className="-mx-6 -mb-4 mt-6 flex flex-col gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm">
          {errorCount > 0 ? (
            <span className="font-medium text-red-600">
              {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} attention
            </span>
          ) : (
            <span className="text-gray-600">Fields marked * are required</span>
          )}
        </div>
        <div className="flex flex-col-reverse gap-3 sm:flex-row">
          <button type="button" onClick={onCancel} disabled={isSaving} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="submit" disabled={isSaving} className={primaryButtonClass}>
            {isSaving ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update Vendor' : 'Create Vendor'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default VendorForm;
